import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutGrid, ChevronDown, ArrowRight } from 'lucide-react';
import logo from '../logo-optimized.jpg';
import {
  CATEGORY_GROUPS,
  PRIMARY_NAV_LINKS,
  UTILITY_NAV_LINKS,
} from '../data/categories';

/**
 * True when the current location matches a category link. Virtual tabs on
 * `/facilities` use router state to decide which entry is highlighted.
 */
const isCategoryActive = (item, location) => {
  if (location.pathname !== item.path) return false;

  // Virtual tabs on /facilities (Amenities, Business & Services) use router
  // state to decide which entry is current; the plain "Facilities" entry is
  // only current when no virtual tab is selected.
  const tab = location.state?.activeTab;
  if (item.state?.activeTab) return tab === item.state.activeTab;
  if (item.path === '/facilities') return !tab;
  return true;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Any navigation closes every overlay.
  useEffect(() => {
    setIsOpen(false);
    setIsMenuOpen(false);
    setIsMobileCategoriesOpen(false);
  }, [location.pathname, location.state]);

  // Lock background scrolling while the mobile drawer is open, and always
  // release it on unmount so a navigation from inside the drawer cannot leave
  // the page permanently unscrollable.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen]);

  // Close the desktop mega-menu on Escape and on outside click.
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    const handleClickOutside = (event) => {
      const clickedMenu = menuRef.current?.contains(event.target);
      const clickedButton = menuButtonRef.current?.contains(event.target);
      if (!clickedMenu && !clickedButton) setIsMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const goToCategory = useCallback((item) => {
    setIsMenuOpen(false);
    setIsOpen(false);
    if (item.state) {
      navigate(item.path, { state: item.state });
    } else {
      navigate(item.path);
    }
  }, [navigate]);

  const standaloneNavLinks = [...PRIMARY_NAV_LINKS, ...UTILITY_NAV_LINKS];
  const standaloneNavLinkIsActive = standaloneNavLinks.some((link) => location.pathname === link.path);
  const categoriesContainCurrentPath = !standaloneNavLinkIsActive && CATEGORY_GROUPS
    .flatMap((group) => group.items)
    .some((item) => isCategoryActive(item, location));

  const totalCategories = CATEGORY_GROUPS.reduce((total, group) => total + group.items.length, 0);

  const linkClasses = (isActive) =>
    `${isActive ? 'text-fud-gold bg-white/10' : 'hover:text-fud-gold hover:bg-white/5'} px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap`;

  return (
    <nav className="bg-fud-green text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          <div className="flex items-center min-w-0">
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
              <div className="bg-white p-1 rounded-lg group-hover:rotate-12 transition-transform shadow-sm flex items-center justify-center">
                <img src={logo} alt="FUD Logo" className="h-7 w-7 object-contain" />
              </div>
              <span className="font-bold text-lg sm:text-xl tracking-tight text-white">FUDChat.com</span>
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-0.5 lg:gap-1">
            {PRIMARY_NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={linkClasses(location.pathname === link.path)}
              >
                {link.name}
              </Link>
            ))}

            {/* Categories mega-menu trigger */}
            <div className="relative">
              <button
                ref={menuButtonRef}
                type="button"
                onClick={() => setIsMenuOpen((open) => !open)}
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
                aria-controls="category-mega-menu"
                className={`${
                  isMenuOpen || categoriesContainCurrentPath
                    ? 'text-fud-gold bg-white/10'
                    : 'hover:text-fud-gold hover:bg-white/5'
                } flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap`}
              >
                <LayoutGrid className="h-4 w-4" />
                Categories
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isMenuOpen && (
                <div
                  id="category-mega-menu"
                  ref={menuRef}
                  className="absolute right-0 mt-3 w-[min(46rem,calc(100vw-1.5rem))] max-h-[calc(100vh-6rem)] overflow-y-auto bg-white text-fud-dark rounded-3xl shadow-2xl border border-fud-gray p-5 sm:p-6 z-50 animate-slide-in-top"
                >
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-sm font-black uppercase tracking-widest text-fud-dark">
                        Browse Categories
                      </h2>
                      <p className="text-xs text-fud-gray-dark font-medium mt-0.5">
                        Everything on campus, one tap away
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-[10px] font-black uppercase tracking-widest text-fud-green bg-fud-green/10 px-2.5 py-1 rounded-full">
                      {totalCategories} sections
                    </span>
                  </div>

                  {CATEGORY_GROUPS.map((group) => (
                    <div key={group.label} className="mb-5 last:mb-0">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-fud-gray-dark mb-3">
                        {group.label}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const active = isCategoryActive(item, location);
                          return (
                            <button
                              key={`${group.label}-${item.name}`}
                              type="button"
                              onClick={() => goToCategory(item)}
                              className={`text-left flex items-start gap-3 p-3 rounded-2xl border transition-all duration-200 group active:scale-[0.98] ${
                                active
                                  ? 'border-fud-green bg-fud-green/5'
                                  : 'border-fud-gray/70 hover:border-fud-green/40 hover:bg-fud-green/5'
                              }`}
                            >
                              <span
                                className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                                  active
                                    ? 'bg-fud-green text-white'
                                    : 'bg-fud-gray-light text-fud-green group-hover:bg-fud-green group-hover:text-white'
                                }`}
                              >
                                <Icon className="h-[18px] w-[18px]" />
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm font-bold text-fud-dark leading-tight">
                                  {item.name}
                                </span>
                                <span className="block text-[11px] text-fud-gray-dark font-medium leading-snug mt-0.5 line-clamp-2">
                                  {item.description}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 mt-1 border-t border-fud-gray flex flex-wrap items-center justify-between gap-3">
                    <Link
                      to="/about"
                      className="text-xs font-bold text-fud-gray-dark hover:text-fud-green transition-colors"
                    >
                      About FUDChat
                    </Link>
                    <Link
                      to="/chat"
                      className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-fud-green hover:text-fud-green-dark transition-colors"
                    >
                      Ask the AI assistant <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {UTILITY_NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={linkClasses(location.pathname === link.path)}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="lg:hidden flex-shrink-0">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md hover:text-fud-gold focus:outline-none"
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-navigation"
        aria-hidden={!isOpen}
        className={`lg:hidden bg-fud-green-dark transition-all duration-300 ease-in-out shadow-2xl ${isOpen ? 'max-h-[85vh] opacity-100 border-t border-white/5 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'}`}
      >
        <div className="px-4 pt-4 pb-8 space-y-2 flex flex-col items-stretch">
          {PRIMARY_NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`w-full text-center px-4 py-3.5 rounded-2xl text-base font-bold transition-all active:scale-95 ${
                location.pathname === link.path
                  ? 'text-fud-gold bg-white/10'
                  : 'text-white/90 hover:bg-white/5 hover:text-white'
              }`}
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </Link>
          ))}

          {/* Collapsible categories in the mobile drawer */}
          <button
            type="button"
            onClick={() => setIsMobileCategoriesOpen((open) => !open)}
            aria-expanded={isMobileCategoriesOpen}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl text-base font-bold transition-all active:scale-95 ${
              categoriesContainCurrentPath ? 'text-fud-gold bg-white/10' : 'text-white/90 hover:bg-white/5'
            }`}
          >
            <LayoutGrid className="h-5 w-5" />
            Categories
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isMobileCategoriesOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isMobileCategoriesOpen && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pb-1 animate-slide-in-top">
              {CATEGORY_GROUPS.flatMap((group) => group.items).map((item) => {
                const Icon = item.icon;
                const active = isCategoryActive(item, location);
                return (
                  <button
                    key={`mobile-${item.name}`}
                    type="button"
                    onClick={() => goToCategory(item)}
                    className={`flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border text-center transition-all active:scale-95 ${
                      active
                        ? 'border-fud-gold/50 bg-white/10 text-fud-gold'
                        : 'border-white/10 text-white/80 hover:bg-white/5'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-bold leading-tight">{item.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {UTILITY_NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`w-full text-center px-4 py-3.5 rounded-2xl text-base font-bold transition-all active:scale-95 ${
                location.pathname === link.path 
                  ? 'text-fud-gold bg-white/10' 
                  : 'text-white/90 hover:bg-white/5 hover:text-white'
              }`}
              onClick={() => setIsOpen(false)}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
